import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { 
  Autocomplete, 
  FormControl, 
  Typography, 
  Snackbar, 
  Alert, 
  Tooltip, 
  Container, 
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchServices } from "../../../redux/slices/services/services/Services";
import { getAllBussinessServices } from "../../../redux/slices/services/bussinessServices/BussinessSerives";
import { fetchDegreeProgramData } from "../../../redux/slices/mca/degreeProgram/degreeProgram";
import { useNavigate, useParams } from "react-router-dom";
import LeftNavigationBar from "../../../navbars/LeftNavigationBar";
import { HelpOutline } from "@material-ui/icons";
import { updateDPBeneficiary, getDPBeneficiaryById } from "../../../redux/slices/mca/dPBeneficiaries/dPBeneficiaries";
import { getAllCompanies } from "../../../redux/slices/mca/company/company";

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 600,
    margin: "auto",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  typeSelector: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  }
}));

const DPBeneficiariesEditForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams(); // Get the ID from the URL
  
  // Type selection state
  const [type, setType] = useState("degreeprogram");
  
  // Common fields
  const [noOfCandidates, setNoOfCandidate] = useState("");
  const [batch, setBatch] = useState("");
  
  // Degree program specific fields
  const [college, setCollege] = useState("");
  const [programme, setProgramme] = useState("");
  
  // Skilling specific fields
  const [hours, setHours] = useState("");
  const [duration, setDuration] = useState("");
  
  // Service and related selections
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBusinessService, setSelectedBusinessService] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Redux state
  const companyData = useSelector((state) => state.companies.companies);
  const serviceData = useSelector((state) => state.service.serviceData);
  const businessServiceData = useSelector((state) => state.businessService.businessServiceData);
  const degreeProgramData = useSelector((state) => state.degreeProgram.degreeProgramData);
  
  // Filtered options
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredDegreePrograms, setFilteredDegreePrograms] = useState([]);
  
  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchServices());
      dispatch(getAllBussinessServices());
      dispatch(fetchDegreeProgramData());
      dispatch(getAllCompanies());
      dispatch(getDPBeneficiaryById(id)); // Fetch the beneficiary data by ID
    } else {
      navigate('/degreeprogram/dp-beneficiaries-control'); // Redirect if ID is not valid
    }
  }, [dispatch, id, navigate]);

  const beneficiary = useSelector((state) => state.dpBeneficiaries.selectedBeneficiary);

  useEffect(() => {
    if (beneficiary) {
      // Set the type based on beneficiary data
      setType(beneficiary.type || "degreeprogram");
      
      // Set common fields
      setBatch(beneficiary.batch || "");
      setNoOfCandidate(beneficiary.noOfCandidates || "");
      
      // Set type-specific fields
      if (beneficiary.type === "degreeprogram" || !beneficiary.type) {
        setCollege(beneficiary.college || "");
        setProgramme(beneficiary.programme || "");
      } else if (beneficiary.type === "skilling") {
        setHours(beneficiary.hours || "");
        setDuration(beneficiary.duration || "");
      }
      
      // Set relationship fields
      setSelectedBusinessService(beneficiary.business_service || null);
      setSelectedService(beneficiary.service || null);
      setSelectedProgram(beneficiary.degree_program || null);
      setSelectedCompany(beneficiary.company || null);
    }
  }, [beneficiary]);

  useEffect(() => {
    if (selectedBusinessService) {
      const filtered = serviceData.filter(
        (service) => service.business_services?._id === selectedBusinessService._id
      );
      setFilteredServices(filtered);
    } else {
      setFilteredServices([]);
    }
  }, [selectedBusinessService, serviceData]);

  useEffect(() => {
    if (selectedService) {
      const filteredPrograms = degreeProgramData.filter(
        (program) => program.service?._id === selectedService._id
      );
      setFilteredDegreePrograms(filteredPrograms);
    } else {
      setFilteredDegreePrograms(degreeProgramData);
    }
  }, [selectedService, degreeProgramData]);

  const handleTypeChange = (event) => {
    setType(event.target.value);
  };

  const handleServiceChange = (_, newValue) => {
    setSelectedService(newValue);
  };

  const handleBussinessServiceChange = (_, newValue) => {
    setSelectedBusinessService(newValue);
  };

  const handleProgramChange = (_, newValue) => {
    setSelectedProgram(newValue);
  };

  const handleCompanyChange = (_, newValue) => {
    setSelectedCompany(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Base form data with common fields
    const formData = {
      type,
      noOfCandidates,
      batch,
      business_service: selectedBusinessService?._id,
      service: selectedService?._id,
      degree_program: selectedProgram?._id,
      company: selectedCompany?._id,
    };

    // Add type-specific fields
    if (type === "degreeprogram") {
      formData.college = college;
      formData.programme = programme;
    } else if (type === "skilling") {
      formData.hours = hours;
      formData.duration = duration;
    }

    try {
      const result = await dispatch(updateDPBeneficiary({ id, beneficiaryData: formData })).unwrap();
      
      // Show success message
      setSnackbar({ 
        open: true, 
        message: "Beneficiary updated successfully", 
        severity: 'success' 
      });

      // Navigate after a short delay
      setTimeout(() => {
        navigate('/degreeprogram/dp-beneficiaries-control');
      }, 1500);
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Show error message from the API response if available
      let errorMessage = 'An error occurred';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        // Try to extract error message from different possible response structures
        const messages = error.response.data.message;
        if (Array.isArray(messages) && messages.length > 0) {
          errorMessage = messages[0].value || errorMessage;
        } else if (typeof messages === 'string') {
          errorMessage = messages;
        }
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBack = () => {
    navigate(-1); // Navigate to the previous page
  };

  return (
    <LeftNavigationBar
      Content={
        <Container component="main" maxWidth="md">
          <Paper elevation={0}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mt={2}
              mb={2}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
              >
                Back
              </Button>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flex: 1
              }}>
                <Typography
                  variant="h4"
                  sx={{
                    position: "relative",
                    padding: 0,
                    margin: 0,
                    fontWeight: 300,
                    fontSize: { xs: "32px", sm: "40px" },
                    color: "#747474",
                    textAlign: "center",
                    textTransform: "uppercase",
                    paddingBottom: "5px",
                    "&::before": {
                      content: '""',
                      width: "28px",
                      height: "5px",
                      display: "block",
                      position: "absolute",
                      bottom: "3px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#747474",
                    },
                    "&::after": {
                      content: '""',
                      width: "100px",
                      height: "1px",
                      display: "block",
                      position: "relative",
                      marginTop: "5px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#747474",
                    },
                  }}
                >
                  Edit Beneficiaries
                </Typography>

                <Tooltip
                  title="This is where you can edit beneficiaries for degree program or skilling."
                  arrow
                >
                  <HelpOutline
                    sx={{ color: "#747474", fontSize: "24px", cursor: "pointer" }}
                  />
                </Tooltip>
              </Box>
            </Box>
            <form
              style={{
                border: "2px dotted #D3D3D3",
                padding: "20px",
                borderRadius: "8px",
              }}
              onSubmit={handleSubmit}>
              
              {/* Type Selection */}
              <Box className={classes.typeSelector}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Select Type</FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={type}
                    onChange={handleTypeChange}
                  >
                    <FormControlLabel 
                      value="degreeprogram" 
                      control={<Radio />} 
                      label="Degree Program" 
                    />
                    <FormControlLabel 
                      value="skilling" 
                      control={<Radio />} 
                      label="Skilling" 
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Common service selection fields */}
              <FormControl fullWidth>
                <Autocomplete
                  id="Business Services"
                  options={businessServiceData || []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={selectedBusinessService}
                  onChange={handleBussinessServiceChange}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Business Services"
                      fullWidth
                    />
                  )}
                />
              </FormControl>
              <br /> <br />
              <FormControl fullWidth>
                <Autocomplete
                  id="service"
                  options={filteredServices || []}
                  getOptionLabel={(option) => option?.title || ""}
                  value={selectedService}
                  onChange={handleServiceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Service"
                      fullWidth
                      required
                    />
                  )}
                />
              </FormControl>
              <br /><br />
              <FormControl fullWidth>
                <Autocomplete
                  id="degree_program"
                  options={filteredDegreePrograms || []}
                  getOptionLabel={(option) =>
                    option ? option.program_name : ""
                  }
                  value={selectedProgram}
                  onChange={handleProgramChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Program"
                      fullWidth
                    />
                  )}
                />
              </FormControl>
              <br /><br />
              <FormControl fullWidth>
                <Autocomplete
                  id="Company"
                  options={companyData || []}
                  getOptionLabel={(option) => option?.companyName || ""}
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Company"
                      fullWidth
                    />
                  )}
                />
              </FormControl>

              {/* Common field: No. of Candidates */}
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                type="number"
                label="No. Of Candidates"
                value={noOfCandidates}
                onChange={(e) => setNoOfCandidate(e.target.value)}
              />

              {/* Common field: Batch */}
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label="Batch"
                value={batch}
                onChange={(e) => setBatch(e.target.value)}
              />

              {/* Conditional fields based on selected type */}
              {type === "degreeprogram" ? (
                <>
                  {/* Degree Program specific fields */}
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="College"
                    value={college}
                    onChange={(e) => setCollege(e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="Programme"
                    value={programme}
                    onChange={(e) => setProgramme(e.target.value)}
                  />
                </>
              ) : (
                <>
                  {/* Skilling specific fields */}
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="Hours"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="Duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  />
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                style={{
                  display: "block",
                  margin: "24px auto 0", // centers the button horizontally
                  backgroundColor: " #1976d2", // blue
                  color: "#fff",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                {isSubmitting ? "Updating..." : "Update Beneficiary"}
              </Button>
            </form>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                variant="filled"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Paper>
        </Container>
      }
    />
  );
};

export default DPBeneficiariesEditForm;